import React, { useEffect, useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { Button, ButtonType } from '@/components/atoms/button'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/atoms/modal'
import { Option, Select } from '@/components/atoms/select'
import { RadioGroupField } from '@/components/molecules/radio-group-field'
import type { SalesSubjectMasterType } from '@/models/sales-manager/sales'
import { DisplayDay, RestaurantDisplaySubject, RestaurantResolvedDisplaySubject } from '@/models/restaurant-manager'

type Props = {
  salesSubjectMasters: SalesSubjectMasterType[]
  usedSubjectIds: string[]
  editingSubject: RestaurantResolvedDisplaySubject | null
  isLoadingOptions: boolean
  onCancel: () => void
  onSave: (subject: Pick<RestaurantDisplaySubject, 'subjectId' | 'displayDay'>) => Promise<void>
}

type SubjectOption = {
  id: string
  label: string
  disabled?: boolean
}

const displayDayOptions: Array<{ value: DisplayDay; labelKey: string }> = [
  { value: 'sameDay', labelKey: 'RestaurantManager.DisplayDaySameDay' },
  { value: 'nextDay', labelKey: 'RestaurantManager.DisplayDayNextDay' },
]

type FormValue = Pick<RestaurantDisplaySubject, 'subjectId' | 'displayDay'>

export const DisplaySubjectModal: React.FC<Props> = ({
  salesSubjectMasters,
  usedSubjectIds,
  editingSubject,
  isLoadingOptions,
  onCancel,
  onSave,
}) => {
  const { t } = useTranslation()
  const [isSaving, setIsSaving] = useState(false)
  const { control, handleSubmit, reset } = useForm<FormValue>({
    defaultValues: {
      subjectId: '',
      displayDay: 'sameDay',
    },
  })
  const selectedSubjectId = useWatch({ control, name: 'subjectId' })

  const currentSubject = useMemo(
    () => salesSubjectMasters.find(subject => subject.id === editingSubject?.subjectId) ?? null,
    [editingSubject?.subjectId, salesSubjectMasters],
  )

  useEffect(() => {
    reset({
      subjectId: editingSubject?.subjectId ?? '',
      displayDay: editingSubject?.displayDay ?? 'sameDay',
    })
  }, [editingSubject, reset])

  const subjectOptions = useMemo<SubjectOption[]>(() => {
    const currentOption: SubjectOption | null = editingSubject
      ? {
          id: editingSubject.subjectId,
          label: editingSubject.subjectName,
          disabled: !currentSubject,
        }
      : null

    const options = salesSubjectMasters
      .filter(subject => !usedSubjectIds.includes(subject.id) || subject.id === editingSubject?.subjectId)
      .map(subject => ({
        id: subject.id,
        label: subject.subjectName,
      }))

    if (currentOption && !options.some(option => option.id === currentOption.id)) {
      return [currentOption, ...options]
    }

    return options
  }, [currentSubject, editingSubject, salesSubjectMasters, usedSubjectIds])

  const canSave = !!selectedSubjectId && !isLoadingOptions

  const handleSave = handleSubmit(async value => {
    setIsSaving(true)
    try {
      await onSave(value)
    } finally {
      setIsSaving(false)
    }
  })

  return (
    <Modal isLoading={isSaving || isLoadingOptions} customCss={modalContainerStyle}>
      <ModalHeader>{t(editingSubject ? 'RestaurantManager.EditDisplaySubject' : 'RestaurantManager.AddDisplaySubject')}</ModalHeader>
      <ModalBody customCss={modalBodyStyle}>
        <div css={bodyContentStyle}>
          <div>
            <div css={inputTitleTextStyle}>
              {t('Subject')}
              <div css={requireLabelTextStyle}>※</div>
            </div>
            <Controller
              control={control}
              name="subjectId"
              render={({ field: { onChange, value } }) => (
                <Select
                  value={value || undefined}
                  onChange={nextValue => onChange(String(nextValue))}
                  disabled={isLoadingOptions}
                  placeholder={t('RestaurantManager.SelectDisplaySubjectPlaceholder')}
                  placeHolderColor="#BFBFBF"
                  showSearch
                  filterOption={true}
                  optionFilterProp="children"
                  notFoundContent={t('No data available')}
                  customStyle={css({ paddingBottom: 0 })}
                >
                  {subjectOptions.map(option => (
                    <Option key={option.id} value={option.id} disabled={option.disabled}>
                      {option.disabled ? `${option.label} (${t('RestaurantManager.MissingMaster')})` : option.label}
                    </Option>
                  ))}
                </Select>
              )}
            />
          </div>

          <div>
            <div css={inputTitleTextStyle}>
              {t('RestaurantManager.DisplayDay')}
              <div css={requireLabelTextStyle}>※</div>
            </div>
            <Controller
              control={control}
              name="displayDay"
              render={({ field: { onChange, value } }) => (
                <RadioGroupField
                  value={value}
                  name="restaurant-display-day"
                  disabled={isLoadingOptions}
                  items={displayDayOptions.map(option => ({
                    value: option.value,
                    label: t(option.labelKey),
                  }))}
                  onChange={event => onChange(event.currentTarget.value as DisplayDay)}
                  groupCss={css({ gap: 24 })}
                  style={{ radioSize: 18, labelMargin: 5 }}
                />
              )}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button buttonType={ButtonType.Secondary} width={110} height={38} fontSize={14} onClick={onCancel}>
          {t('Cancel')}
        </Button>
        <Button
          buttonType={ButtonType.Primary}
          width={110}
          height={38}
          fontSize={14}
          marginLeft={16}
          isDisabled={!canSave || isSaving}
          onClick={() => {
            handleSave()
          }}
        >
          {t('Save')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

const modalContainerStyle = css({
  height: 'auto',
})

const bodyContentStyle = css({
  padding: '32px',
  backgroundColor: '#FFFFFF',
  border: '1px solid #CCCCCC',
  borderRadius: '5px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
})

const modalBodyStyle = css({
  padding: 32,
})

const inputTitleTextStyle = css({
  display: 'flex',
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: '0.6px',
  color: '#676767',
  paddingBottom: 12,
})

const requireLabelTextStyle = css({
  fontSize: 8,
  fontWeight: 'bold',
  letterSpacing: '0.4px',
  color: '#676767',
  paddingLeft: 8,
})
